import { CheckCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/shared/lib/cn'

import { useMarkAllAsReadMutation } from '../hooks/useNotifications'
import type { NotificationTab } from '../types/notification.types'

interface MarkAllAsReadButtonProps {
  /**
   * Tamaño visual del botón:
   *   - {@code 'md'} (default): pill brutalista con icono+texto, para la cabecera
   *     de {@code /notifications}.
   *   - {@code 'sm'}: pill compacto, para el dropdown del bell.
   */
  size?: 'sm' | 'md'
  /**
   * Si {@code true}, el botón se renderiza deshabilitado (visual + pointer-events).
   * Útil para reservar slot fijo en la cabecera cuando no hay nada que marcar —
   * evita que la acción salte de posición al desaparecer.
   */
  disabled?: boolean
  /** Pestaña sobre la que actúa; sin ella, todos los avisos. */
  tab?: NotificationTab
}

/**
 * Botón "Leer todas" para marcar todas las notificaciones no leídas como
 * leídas. Mismo patrón pill brutalista que {@link DeleteAllReadButton} para
 * mantener consistencia visual y de layout en las cabeceras donde conviven.
 * El hook ya hace optimistic update — el badge del bell + las filas del
 * listado se actualizan instantáneamente.
 */
export function MarkAllAsReadButton({
  size = 'md',
  disabled = false,
  tab,
}: MarkAllAsReadButtonProps) {
  const { t } = useTranslation()
  const markAll = useMarkAllAsReadMutation(tab)
  const isDisabled = disabled || markAll.isPending

  const baseClass =
    size === 'md'
      ? 'brutal-sm brutal-press inline-flex items-center gap-1.5 rounded-md bg-yellow px-3 py-1.5 font-brutal text-[10px] font-bold uppercase tracking-widest text-foreground disabled:cursor-not-allowed disabled:opacity-40'
      : 'brutal-sm brutal-press inline-flex items-center gap-1 rounded-md bg-yellow px-2 py-1 font-brutal text-[10px] font-bold uppercase tracking-widest text-foreground disabled:cursor-not-allowed disabled:opacity-40'

  const iconSize = size === 'md' ? 14 : 12

  return (
    <button
      type="button"
      onClick={() => markAll.mutate()}
      disabled={isDisabled}
      className={cn(baseClass)}
    >
      <CheckCheck size={iconSize} aria-hidden="true" strokeWidth={2.5} />
      <span>{t('notifications.actions.markAllAsRead')}</span>
    </button>
  )
}
